import React, { useEffect } from "react";
import { TextField, MenuItem, Box, Typography } from "@mui/material";
import {
  scrollToTop,
  safeNumber,
  parseCurrencyToNumber,
  NumberFormatCustom,
} from "../../shared/formHelpers";
import { BOND_STEP_ESTATE_DETAILS } from "../../shared/bondWizardStepPropTypes";

/**
 * GeneralInformationForm — estate-level questions for probate bonds.
 *
 * Extracted from ProbateBond.jsx (formerly inline GeneralInformationForm).
 * Renders Yes/No questions about the estate with conditional explanation fields.
 */
const EstateDetails = ({ data = {}, updateData, globalData, errors }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateData({ ...data, [name]: value });
  };

  const handleExplanationChange = (e) => {
    const { name, value } = e.target;
    updateData({
      ...data,
      explanations: {
        ...data?.explanations,
        [name]: value || "",
      },
    });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  const explanations = data?.explanations || {
    ongoingBusiness: "",
    didLastWillAndTestament: "",
    indebtedGuardians: "",
    replacingFiduciary: "",
    replacingPriorBond: "",
    demandedByInterestedParty: "",
    jointControlExercised: "",
    supplementalNeedsTrust: "",
    outstandingLawsuitExplanation: "",
    bankruptOrInsolventExplanation: "",
    isDeclined: "",
    anyDisputes: "",
  };

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      data-testid="bond-wizard-step-general"
      sx={{
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        General Information
      </Typography>

      <Box className="grid grid-cols-1 gap-4">
        <TextField
          label="Estimated Assets"
          name="estAssets"
          value={data.estAssets || null}
          onChange={handleChange}
          fullWidth
          error={!!errors.estAssets}
          helperText={errors.estAssets}
          slotProps={{ input: {
            inputComponent: NumberFormatCustom,
          }}}
        />

        {/* Ongoing Business */}
        <TextField
          select
          label="Will any of the business of the deceased be operated by the fiduciary?"
          name="ongoingBusiness"
          value={data.ongoingBusiness || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.ongoingBusiness}
          helperText={errors.ongoingBusiness}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.ongoingBusiness === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="ongoingBusiness"
            value={explanations.ongoingBusiness || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Last Will and Testament */}
        <TextField
          select
          label="Did deceased execute a last will and testament?"
          name="didLastWillAndTestament"
          value={data.didLastWillAndTestament}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.didLastWillAndTestament}
          helperText={errors.didLastWillAndTestament}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>

        {/* Indebted Guardians */}
        <TextField
          select
          label="Are any of the principals indebted to the Estate?"
          name="indebtedGuardians"
          value={data.indebtedGuardians || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.indebtedGuardians}
          helperText={errors.indebtedGuardians}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.indebtedGuardians === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="indebtedGuardians"
            value={explanations.indebtedGuardians || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Replacing Fiduciary */}
        <TextField
          select
          label="Does applicant replace any previous fiduciary?"
          name="replacingFiduciary"
          value={data.replacingFiduciary || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.replacingFiduciary}
          helperText={errors.replacingFiduciary}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.replacingFiduciary === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="replacingFiduciary"
            value={explanations.replacingFiduciary || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Replacing Prior Bond */}
        <TextField
          select
          label="Does this bond replace any prior bond?"
          name="replacingPriorBond"
          value={data.replacingPriorBond || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.replacingPriorBond}
          helperText={errors.replacingPriorBond}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>

        {/* Declined By Surety */}
        <TextField
          select
          label="Has the applicant for this bond been declined by another Surety?"
          name="isDeclined"
          value={data.isDeclined || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.isDeclined}
          helperText={errors.isDeclined}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.isDeclined === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="isDeclined"
            value={explanations.isDeclined || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Demanded by Interested Party */}
        <TextField
          select
          label="Is this bond required on the demand of an interested party other than the Court?"
          name="demandedByInterestedParty"
          value={data.demandedByInterestedParty || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.demandedByInterestedParty}
          helperText={errors.demandedByInterestedParty}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.demandedByInterestedParty === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="demandedByInterestedParty"
            value={explanations.demandedByInterestedParty || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Outstanding Lawsuit */}
        <TextField
          select
          label="Does the applicant have any outstanding lawsuits, judgment, or liens?"
          name="outstandingLawsuit"
          value={data.outstandingLawsuit || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.outstandingLawsuit}
          helperText={errors.outstandingLawsuit}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.outstandingLawsuit === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="outstandingLawsuitExplanation"
            value={explanations.outstandingLawsuitExplanation || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Ever Bankrupt */}
        <TextField
          select
          label="Has the applicant ever been bankrupt or insolvent?"
          name="bankruptOrInsolvent"
          value={data.bankruptOrInsolvent || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.bankruptOrInsolvent}
          helperText={errors.bankruptOrInsolvent}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.bankruptOrInsolvent === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="bankruptOrInsolventExplanation"
            value={explanations.bankruptOrInsolventExplanation || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Any disputes between heirs? */}
        <TextField
          select
          label="Any disputes between heirs?"
          name="anyDisputes"
          value={data.anyDisputes || "No"}
          onChange={handleChange}
          fullWidth
          required
          error={!!errors.anyDisputes}
          helperText={errors.anyDisputes}
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
        {data.anyDisputes === "Yes" && (
          <TextField
            label="Provide an explanation"
            name="anyDisputes"
            value={explanations.anyDisputes || ""}
            onChange={handleExplanationChange}
            fullWidth
          />
        )}

        {/* Additional Question: Request for Authorization */}
        {safeNumber(parseCurrencyToNumber(globalData?.basicInformation?.bondAmount)) >= 250000 && (
          <>
            <Box
              sx={{
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                backgroundColor: "#f9fafb",
                padding: "16px",
                margin: "16px 0",
              }}
            >
              <Typography variant="h6" sx={{ marginBottom: "2px" }}>
                Authorization to Obtain Underwriting Information
              </Typography>
              <Typography>
                Do you fully understand and agree, by submitting this request
                for approval you are providing authorization for Probate Bond
                Pros, surety companies and/or third-party brokers to verify
                information you provided as well as permission to obtain your
                consumer information from any source which would be necessary to
                determine bonding eligibility? This includes obtaining credit
                reports at time of application, during any review or renewal, at
                the time of any potential or actual claim, and/or for any other
                legitimate purposes determined by the surety in its reasonable
                discretion. This information will be held in the strictest
                confidence. Do you agree?
              </Typography>
            </Box>

            <TextField
              select
              label="Authorization to obtain underwriting information?"
              name="requestForAuthorization"
              value={data.requestForAuthorization || ""}
              onChange={handleChange}
              fullWidth
              required
            >
              <MenuItem value="" disabled>
                Select an option
              </MenuItem>
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </TextField>
          </>
        )}
      </Box>
    </Box>
  );
};

EstateDetails.propTypes = BOND_STEP_ESTATE_DETAILS;

export default EstateDetails;
